import React, { FC, useEffect, useState } from "react"
import { KButton, KDropdown, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
//@ts-ignore
import ProfileIcon from "../../../../assets/account_icons/profile.svg"
//@ts-ignore
import ArrowLeftIcon from "../../../../assets/account_icons//arrow-left.svg"
//@ts-ignore
import WorldIcon from "../../../../assets/account_icons//world.svg"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { countryOptions } from "./constants"
//@ts-ignore
import ChevronDown from "../../../../assets/account_icons//chevron-down.svg"
//@ts-ignore
import UserIcon from "../../../../assets/account_icons//user.svg"
//@ts-ignore
import BriefCaseIcon from "../../../../assets/account_icons//briefcase.svg"
import axios from "axios"
import { mapToCamelCase, mapToSnakeCase } from "../../../../utility"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { toast } from "react-toastify"
import { users } from "../../../../apis"
import { lang } from "../../../../constants/languages"
import Loading from "../../../ui/Loading"

type UserContractProps = {
  viewOnly: boolean
  userId?: number
  onSubmitClose?: () => void
  hideTitle?: boolean
}

export interface UserInformation {
  id?: number
  type: string
  name: string
  address: string
  country: string
  birthday: string
  taxId?: string
  companyRegistrationNo?: string
}

const initialForm: UserInformation = {
  type: "",
  name: "",
  address: "",
  country: "",
  birthday: "",
  taxId: "",
  companyRegistrationNo: ""
}

enum TYPE_OPTION {
  INDIVIDUAL = "individual",
  COMPANY = "company"
}

const typeOptions: KSelectOption[] = [
  { label: "Individual", value: 1, value2: TYPE_OPTION.INDIVIDUAL },
  { label: "Company", value: 2, value2: TYPE_OPTION.COMPANY }
]

enum STEP_OPTIONS {
  TypeSelection,
  FillInformation
}

const UserContract: FC<UserContractProps> = (props) => {
  const [form, setForm] = useState<UserInformation>({ ...initialForm })
  const [loading, setLoading] = useState(false)
  const [selectedStep, setSelectedStep] = useState(STEP_OPTIONS.TypeSelection)
  const [confirmAge, setConfirmAge] = useState(false)
  const [foundCountry, setFoundCountry] = useState<string>()
  const [initialLoad, setInitialLoad] = useState(true)
  const [addressFields, setAddressFields] = useState({
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: ""
  })
  const [userInformation, setUserInformation] = useState<UserInformation>()

  const buildAddressBlob = (fields: any) => {
    return [fields.address1, fields.address2, fields.city, fields.state, fields.postalCode].join("\n")
  }

  const parseAddressBlob = (text: string) => {
    if (!text) return {}

    const lines = text.split("\n").map((x) => x.trim())

    setAddressFields({
      address1: lines[0] || "",
      address2: lines[1] || "",
      city: lines[2] || "",
      state: lines[3] || "",
      postalCode: lines[4] || ""
    })
  }

  const fetchLocation = async (formToUpdate: UserInformation) => {
    try {
      const GEO_API_KEY = "11f9139a097a4093a313883f7377c332"

      // Get user's IP address
      const ipResponse = await axios.get("https://api.ipify.org/?format=json")
      const userIp = ipResponse.data.ip

      // Get geolocation data based on IP
      const geoResponse = await fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${GEO_API_KEY}&ip=${userIp}`)
      const geoData = await geoResponse.json()

      // Extract country information
      const countryPhone = geoData.calling_code?.replace("+", "")
      const countryName = geoData.country_name

      // Try to find country in countryOptions by name first
      let matchedCountry = countryOptions.find((option) => option.label.toLowerCase() === countryName?.toLowerCase())

      // If not found by name, try to match by phone code
      if (!matchedCountry && countryPhone) {
        matchedCountry = countryOptions.find((option) => option.value.toString() === countryPhone)
      }

      setFoundCountry(matchedCountry?.label)
      setForm({ ...formToUpdate, country: matchedCountry?.label || "" })
    } catch (err) {
      console.error("Error fetching IP location:", err)
      // If geolocation fails, just continue without setting country
    }
  }

  useEffect(() => {
    setLoading(true)
    const userIdParam = props.userId ? `?user_id=${props.userId}` : ""
    users(`/user_information/${userIdParam}`, "GET").then((resp) => {
      setLoading(false)
      if (resp.data.result) {
        const userInformation: UserInformation = mapToCamelCase(resp.data.result)
        setUserInformation(userInformation)
      }
    })
  }, [])

  useEffect(() => {
    const formToUpdate: UserInformation = mapToCamelCase(userInformation)
    setForm(formToUpdate)
    parseAddressBlob(formToUpdate.address)

    if (formToUpdate.type) setSelectedStep(STEP_OPTIONS.FillInformation)

    if (!initialLoad && !foundCountry && !userInformation?.country) {
      fetchLocation(formToUpdate)
    }
    setInitialLoad(false)
  }, [userInformation])

  const checkButtonDisabled = () => {
    if (!form.type || props.viewOnly) {
      return true
    }

    if (!form.name) return true
    if (!form.country) return true
    if (!addressFields.address1) return true
    if (!addressFields.city) return true
    if (!addressFields.postalCode) return true

    if (form.type === "company") {
      if (!form.taxId) return true
    } else if (form.type === "individual" && !props.hideTitle) {
      if (!confirmAge) return true
    }

    return false || loading
  }

  const submitForm = async (form: UserInformation) => {
    setLoading(true)
    const userIdParam = props.userId ? `?user_id=${props.userId}` : ""

    users(
      `/save_user_information/${userIdParam}`,
      "POST",
      mapToSnakeCase({ ...form, address: buildAddressBlob(addressFields) })
    )
      .then((resp) => {
        if (resp.data.result) {
          toast.success("Success!", successFilledToastOptions)
          if (props.onSubmitClose) props.onSubmitClose()
        } else {
          toast.success("Error!", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error!", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const renderTypeOptions = () => {
    return (
      <div className="flex mt-2 gap-8" style={{ width: "100%" }}>
        <div
          data-testid="user-contract-individual-option"
          className="flex flex-col items-center justify-center cursor-pointer"
          style={{ width: "50%", border: "1px solid #11111116", borderRadius: 10, height: 200 }}
          onClick={() => {
            setForm({ ...form, type: TYPE_OPTION.INDIVIDUAL })
            setSelectedStep(STEP_OPTIONS.FillInformation)
          }}
        >
          <img src={UserIcon} width={50} />
          <KTitleSpan text={lang.account.user_contract.individual} fontSize={28} />
        </div>
        <div
          data-testid="user-contract-company-option"
          className="flex flex-col items-center justify-center cursor-pointer"
          style={{ width: "50%", border: "1px solid #11111116", borderRadius: 10, height: 200 }}
          onClick={() => {
            setForm({ ...form, type: TYPE_OPTION.COMPANY })
            setSelectedStep(STEP_OPTIONS.FillInformation)
          }}
        >
          <img src={BriefCaseIcon} width={50} />
          <KTitleSpan text={lang.account.user_contract.company} fontSize={28} />
        </div>
      </div>
    )
  }

  const renderForm = () => {
    if (!form.type) {
      setSelectedStep(STEP_OPTIONS.TypeSelection)
    }

    return (
      <div className={"w-full grid"}>
        <div className="flex gap-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <KInput
              dataTestId="user-contract-name"
              disabled={props.viewOnly}
              value={form.name}
              onChange={(text) => setForm({ ...form, name: text })}
              placeholder={
                form.type === "company"
                  ? lang.account.user_contract.company_name
                  : lang.account.user_contract.full_legal_name
              }
              leftIcon={ProfileIcon}
              fontSize={"14px"}
              padding="14px"
              gap="8px"
            />
          </div>

          <div style={{ width: "100%" }}>
            {props.viewOnly ? (
              <KSpan text={form.country} />
            ) : (
              <KDropdown
                dataTestId="user-contract-country-dropdown"
                selected={form.country ? { label: form.country, value: -1 } : undefined}
                placeholder={lang.account.user_contract.country}
                leftIcon={WorldIcon}
                gap="8px"
                padding="14px"
                height={48}
                onSelect={(selected) => setForm({ ...form, country: (selected as KSelectOption).label })}
                options={countryOptions}
                rightIcon={ChevronDown}
              />
            )}
          </div>
        </div>
        {form.type === TYPE_OPTION.COMPANY && (
          <div>
            <div className={"flex gap-2 mt-2"} style={{}}>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="user-contract-tax-id"
                  disabled={props.viewOnly}
                  value={form.taxId || ""}
                  onChange={(text) => setForm({ ...form, taxId: text })}
                  placeholder={lang.account.user_contract.tax_id}
                  fontSize={"14px"}
                  padding="14px"
                  gap="8px"
                />
              </div>
              <div style={{ width: "50%" }}>
                <KInput
                  dataTestId="user-contract-company-registration-no"
                  disabled={props.viewOnly}
                  value={form.companyRegistrationNo || ""}
                  onChange={(text) => setForm({ ...form, companyRegistrationNo: text })}
                  placeholder={lang.account.user_contract.company_reg_no}
                  fontSize={"14px"}
                  padding="14px"
                  gap="8px"
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <KInput
              dataTestId="user-contract-address-line-1"
              disabled={props.viewOnly}
              value={addressFields.address1}
              onChange={(v) => setAddressFields({ ...addressFields, address1: v })}
              placeholder={lang.account.user_contract.address_line_1}
              fontSize={"14px"}
              padding="14px"
              gap="8px"
            />
          </div>

          <div style={{ width: "100%" }}>
            <KInput
              dataTestId="user-contract-address-line-2"
              disabled={props.viewOnly}
              value={addressFields.address2}
              onChange={(v) => setAddressFields({ ...addressFields, address2: v })}
              placeholder={lang.account.user_contract.address_line_2}
              fontSize={"14px"}
              padding="14px"
              gap="8px"
            />
          </div>
        </div>

        <div className="flex mt-2 gap-2" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <KInput
              dataTestId="user-contract-city"
              disabled={props.viewOnly}
              value={addressFields.city}
              onChange={(v) => setAddressFields({ ...addressFields, city: v })}
              placeholder={lang.account.user_contract.city}
              fontSize={"14px"}
              padding="14px"
              gap="8px"
            />
          </div>
          {form.country === "United States" && (
            <div style={{ width: "100%" }}>
              <KInput
                dataTestId="user-contract-state"
                disabled={props.viewOnly}
                value={addressFields.state}
                onChange={(v) => setAddressFields({ ...addressFields, state: v })}
                placeholder={lang.account.user_contract.state}
                fontSize={"14px"}
                padding="14px"
                gap="8px"
              />
            </div>
          )}
          <div style={{ width: "100%" }}>
            <KInput
              dataTestId="user-contract-postal-code"
              disabled={props.viewOnly}
              value={addressFields.postalCode}
              onChange={(v) => setAddressFields({ ...addressFields, postalCode: v })}
              placeholder={lang.account.user_contract.zip_postal_code}
              fontSize={"14px"}
              padding="14px"
              gap="8px"
            />
          </div>
        </div>

        {form.type === TYPE_OPTION.INDIVIDUAL && !props.viewOnly && !props.hideTitle && (
          <div className={"flex gap-2 mt-4 items-center"} style={{}}>
            <input
              data-testid="user-contract-confirm-age-checkbox"
              checked={confirmAge}
              onChange={() => setConfirmAge(!confirmAge)}
              type="checkbox"
              style={{ accentColor: "black", cursor: "pointer" }}
            />
            <KSpan text={lang.account.user_contract.confirmation_text} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative flex flex-row items-start justify-center" style={{ height: "100%", width: "100%" }}>
      {loading && <Loading />}
      <div
        className={`flex flex-row gap-2.5 !w-full !gap-6"}`}
        style={{
          height: "100%",
          width: "80%"
        }}
      >
        <div className={`flex flex-row justify-start items-start`} style={{ height: "100%", width: "100%" }}>
          <div
            style={{
              height: "100%",
              width: "100%",
              overflowY: "auto"
            }}
          >
            <div className="flex flex-row gap-6">
              {!props.viewOnly && !props.hideTitle && (
                <div className="w-3 pt-0.5 max-[850px]:hidden mr-1">
                  <KButton
                    dataTestId="user-contract-back-button"
                    icon={ArrowLeftIcon}
                    height="36px"
                    width="36px"
                    borderRadius={10}
                    background="#F7F7F7"
                    padding="8px"
                    shadowDisabled
                    onClick={() => {
                      if (selectedStep === STEP_OPTIONS.FillInformation) {
                        setSelectedStep(STEP_OPTIONS.TypeSelection)
                      }
                    }}
                    hoverBackground="#F3F3F3"
                  />
                </div>
              )}

              <div className={"w-full grid"}>
                {!props.hideTitle && (
                  <KSpan
                    text={lang.menu.user_contract}
                    lineHeight={"32px"}
                    fontSize={18}
                    color="#000"
                    fontWeight={500}
                  />
                )}
                {!props.hideTitle && (
                  <div className="mt-2 mb-6">
                    {!props.viewOnly && <KSpan text={lang.account.user_contract.description} />}
                  </div>
                )}

                {selectedStep === STEP_OPTIONS.TypeSelection && renderTypeOptions()}
                {selectedStep === STEP_OPTIONS.FillInformation && renderForm()}

                <div className="flex justify-end mt-6 gap-4">
                  {form.type && !props.viewOnly && (
                    <div>
                      <KButton
                        dataTestId="user-contract-save-button"
                        onClick={() => submitForm(form)}
                        text={lang.account.user_contract.save}
                        disabled={checkButtonDisabled()}
                        background="black"
                        textColor="white"
                        padding="14px"
                        width="100px"
                        height="48px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserContract
